import { forwardRef } from 'react';
import type { Ref } from 'react';
import type { TextInput as RNTextInput } from 'react-native';

import { TextInputBase, TextInputBaseProps } from './shared/TextInputBase';

type TextAreaProps = Omit<TextInputBaseProps, 'multiline' | 'prefix' | 'suffix'>;

/**
 * Use TextArea to collect multiline text from the user. For single-line text,
 * use TextInput.
 *
 * TextArea always takes up the full width of its container. To limit its
 * width, wrap it in a Box or other container component.
 *
 * To use TextArea as an uncontrolled component, leave the `value` prop
 * undefined. To use it as a controlled component, pass `value` and
 * `onChangeText` (or `onChange`). If you need to prefill the input but still
 * want to use an uncontrolled component, use the `defaultValue` prop.
 *
 * You must pass a `title` prop. This is so we can ensure that the input meets
 * accessibility requirements for screen readers.
 *
 * If you want to track impression events ("inputViewEvent"), pass
 * `analyticsName` and `trackImpression`.
 *
 * TextArea supports a subset of [React Native's TextInput props](
 * https://reactnative.dev/docs/textinput). One notable prop is [autoComplete](
 * https://reactnative.dev/docs/textinput#autocomplete) — make sure to include
 * `autoComplete` whenever relevant so the user's device can offer useful
 * suggestions for filling out the input. Another prop to know about is
 * [autoFocus](https://reactnative.dev/docs/textinput#autofocus) — be careful
 * when using `autoFocus` because it can cause confusing behavior. Automatically
 * focusing on a control can make the page scroll on load and cause users to
 * miss context created by content earlier on the page. It can also cause
 * dynamic keyboards to display over the page on touch devices. For
 * screen-readers, it can "teleport" the user to the form control without
 * warning them or announcing anything on the page before the control's label.
 */

const TextArea = forwardRef(function TextArea(props: TextAreaProps, ref: Ref<RNTextInput>) {
  return <TextInputBase multiline={true} {...props} ref={ref} />;
});

export { TextArea, TextAreaProps };
